import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Line, Doughnut, Radar, PolarArea } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import CountUp from 'react-countup';
import { 
  FaUsers, FaClock, FaCheckCircle, FaBolt, 
  FaCircleNotch, FaTrophy, FaSearch, FaUserTag 
} from 'react-icons/fa';

import 'react-datepicker/dist/react-datepicker.css';

// --- Global Chart Font Override ---
ChartJS.defaults.font.family = "'Roboto', sans-serif";
ChartJS.defaults.color = '#64748b'; // slate-500

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, 
  PointElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler
);

const WorkforceProductivity = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(2025, 0, 1), new Date()]);
  const [startDate, endDate] = dateRange;
  const [selectedEmployee, setSelectedEmployee] = useState('All');
  const [searchId, setSearchId] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const mockData = {
        employees: [
          { id: 'EMP001', name: 'Arjun Mehta', jobs: 58, avgTime: 1.2, score: 98, quality: 95, speed: 92, reliability: 99, status: 'Top Performer', trend: [70, 80, 85, 90, 95, 98] },
          { id: 'EMP002', name: 'Priya Sharma', jobs: 52, avgTime: 1.4, score: 94, quality: 88, speed: 96, reliability: 90, status: 'Exceeding', trend: [60, 75, 80, 88, 92, 94] },
          { id: 'EMP003', name: 'Rohan Gupta', jobs: 49, avgTime: 1.5, score: 91, quality: 85, speed: 85, reliability: 92, status: 'On Track', trend: [50, 60, 70, 80, 85, 91] },
          { id: 'EMP004', name: 'Ananya Iyer', jobs: 45, avgTime: 1.8, score: 88, quality: 90, speed: 80, reliability: 85, status: 'On Track', trend: [55, 65, 75, 82, 85, 88] },
          { id: 'EMP005', name: 'Vikram Singh', jobs: 38, avgTime: 2.1, score: 82, quality: 75, speed: 70, reliability: 80, status: 'Needs Review', trend: [40, 50, 65, 70, 78, 82] },
        ],
        globalTrend: [82, 88, 85, 95, 98, 92],
      };
      setData(mockData);
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [dateRange]);

  const filteredEmployees = useMemo(() => {
    if (!data) return [];
    return data.employees.filter(e => {
      const matchName = selectedEmployee === 'All' || e.name === selectedEmployee;
      const matchId = searchId.trim() === '' || e.id.toLowerCase().includes(searchId.toLowerCase());
      return matchName && matchId;
    });
  }, [data, selectedEmployee, searchId]);

  const isIndividual = filteredEmployees.length === 1;
  const target = filteredEmployees[0];

  const mainChartData = isIndividual ? {
    labels: ['QUALITY', 'SPEED', 'RELIABILITY', 'SCORE', 'VOLUME'],
    datasets: [{
      label: target.name,
      data: [target.quality, target.speed, target.reliability, target.score, (target.jobs / 60) * 100],
      backgroundColor: 'rgba(79, 70, 229, 0.1)',
      borderColor: '#4f46e5',
      pointBackgroundColor: '#4f46e5',
      borderWidth: 2,
    }]
  } : {
    labels: filteredEmployees.map(e => e.name.split(' ')[0].toUpperCase()),
    datasets: [{
      data: filteredEmployees.map(e => e.jobs),
      backgroundColor: '#4f46e5',
      borderRadius: 2,
    }]
  };

  return (
    <div className="p-8 bg-[#f8fafc] min-h-screen font-['Roboto'] text-slate-600">
      {/* --- HEADER SECTION --- */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-10 gap-6 border-b border-slate-200 pb-8">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
            Workforce <span className="text-indigo-600">Productivity</span>
          </h1>
          <p className="text-[11px] font-bold text-slate-400 tracking-[0.2em] uppercase mt-1">
            {isIndividual ? `Analytical Profile // ${target.id}` : `Aggregated Team Intelligence // ${filteredEmployees.length} Units`}
          </p>
        </div>

        {/* --- FILTERS --- */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded border border-slate-200 shadow-sm">
            <FaSearch className="text-slate-300 size-3" />
            <input 
              type="text" placeholder="SEARCH ID..." value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="bg-transparent text-[10px] font-bold outline-none w-24 text-slate-800 placeholder:text-slate-300"
            />
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded border border-slate-200 shadow-sm">
            <FaUserTag className="text-indigo-500 size-3" />
            <select 
              value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}
              className="bg-transparent text-[10px] font-bold outline-none cursor-pointer text-slate-800"
            >
              <option value="All">ALL PERSONNEL</option>
              {data?.employees.map(e => <option key={e.id} value={e.name}>{e.name.toUpperCase()}</option>)}
            </select>
          </div>
          <button 
            onClick={() => { setSelectedEmployee('All'); setSearchId(''); }}
            className="text-[10px] font-black px-4 py-2 bg-slate-900 text-white rounded hover:bg-indigo-600 transition-all uppercase tracking-widest"
          >
            Reset
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-96">
          <FaCircleNotch className="animate-spin text-indigo-500 mb-4" size={32} />
          <span className="text-[11px] font-black uppercase tracking-[0.4em] text-slate-400">Syncing Intelligence</span>
        </div>
      ) : (
        <div className="space-y-8">
          {/* --- KPI GRID --- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Avg Cycle Time" val={isIndividual ? target.avgTime : 1.4} unit="H" icon={<FaClock />} />
            <StatCard label="Performance Score" val={isIndividual ? target.score : 91} unit="%" icon={<FaCheckCircle />} />
            <StatCard label="Task Throughput" val={isIndividual ? target.jobs : 242} unit="U" icon={<FaBolt />} />
            <StatCard label="Staff Capacity" val={isIndividual ? 1 : filteredEmployees.length} unit="P" icon={<FaUsers />} />
          </div>

          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-8 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* --- MAIN CHART --- */}
                <div className="bg-white p-8 rounded border border-slate-200 shadow-sm">
                  <h3 className="text-[10px] font-black uppercase text-slate-400 mb-8 tracking-[0.3em] border-l-4 border-indigo-500 pl-4">
                    {isIndividual ? 'Performance Vector' : 'Output Variance'}
                  </h3>
                  <div className="h-64">
                    {isIndividual ? (
                      <Radar data={mainChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                    ) : (
                      <Bar data={mainChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { display: false } } }} />
                    )}
                  </div>
                </div>

                {/* --- SECONDARY CHART --- */}
                <div className="bg-white p-8 rounded border border-slate-200 shadow-sm">
                  <h3 className="text-[10px] font-black uppercase text-slate-400 mb-8 tracking-[0.3em] border-l-4 border-indigo-500 pl-4">
                    {isIndividual ? 'Metric Distribution' : 'Team Allocation'}
                  </h3>
                  <div className="h-64">
                    <Doughnut 
                        data={{
                            labels: ['A', 'B', 'C'],
                            datasets: [{ data: [40, 35, 25], backgroundColor: ['#4f46e5', '#818cf8', '#e2e8f0'], borderWidth: 0 }]
                        }} 
                        options={{ responsive: true, maintainAspectRatio: false, cutout: '85%' }} 
                    />
                  </div>
                </div>
              </div>

              {/* --- TREND LINE --- */}
              <div className="bg-white p-8 rounded border border-slate-200 shadow-sm">
                <h3 className="text-[10px] font-black uppercase text-slate-400 mb-8 tracking-[0.3em] border-l-4 border-indigo-500 pl-4">Historical Efficiency Trend</h3>
                <div className="h-48">
                  <Line 
                    data={{
                      labels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'],
                      datasets: [{
                        label: 'Efficiency',
                        data: isIndividual ? target.trend : data.globalTrend,
                        borderColor: '#4f46e5',
                        backgroundColor: 'rgba(79, 70, 229, 0.05)',
                        fill: true,
                        tension: 0.1,
                        pointRadius: 4,
                        pointBackgroundColor: '#4f46e5'
                      }]
                    }} 
                    options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { grid: { borderDash: [5, 5] } } } }} 
                  />
                </div>
              </div>
            </div>

            {/* --- SIDEBAR RANKING --- */}
            <div className="col-span-12 lg:col-span-4">
              <div className="bg-white rounded border border-slate-200 shadow-sm h-full flex flex-col">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                  <h3 className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em]">Resource Rankings</h3>
                  <FaTrophy className="text-indigo-500 size-4" />
                </div>
                <div className="p-4 flex-grow">
                  {filteredEmployees.map(emp => (
                    <div key={emp.id} className="flex items-center justify-between p-4 mb-2 rounded border border-transparent hover:border-slate-200 hover:bg-slate-50 transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded bg-slate-900 text-white flex items-center justify-center font-black text-sm">
                          {emp.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-slate-900 uppercase tracking-tight">{emp.name}</p>
                          <p className="text-[9px] text-indigo-600 font-bold">{emp.id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-black text-slate-900">{emp.score}%</p>
                        <div className="w-16 h-1 bg-slate-100 mt-1">
                          <div className="h-full bg-indigo-500" style={{ width: `${emp.score}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {isIndividual && (
                  <div className="p-6 bg-slate-900 m-4 rounded">
                    <p className="text-[9px] font-black uppercase text-indigo-400 tracking-widest">System Status</p>
                    <p className="text-lg font-black text-white mt-1 italic uppercase tracking-tighter">{target.status}</p>
                    <button className="w-full mt-6 py-3 border border-white/20 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-[0.2em] transition-all">
                      Generate PDF Report
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, val, unit, icon }) => (
  <div className="bg-white p-6 rounded border border-slate-200 shadow-sm flex items-center justify-between group hover:border-indigo-500 transition-colors">
    <div>
      <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">{label}</p>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-black text-slate-900 tracking-tighter">
          <CountUp end={val} decimals={val % 1 !== 0 ? 1 : 0} duration={1.5} />
        </span>
        <span className="text-[11px] font-black text-indigo-500">{unit}</span>
      </div>
    </div>
    <div className="text-slate-200 group-hover:text-indigo-100 transition-colors">
      {React.cloneElement(icon, { size: 24 })}
    </div>
  </div>
);

export default WorkforceProductivity;